import { Breadcrumb } from "@app/shared/components";
import { contactUsAtom } from "@core/contact-us/atom";
import { URLS } from "@core/shared/utils";
import { Button } from "@design-system/components/Button";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { CheckCircle2, Clock3, Home, MessageSquarePlus } from "lucide-react";

/**
 * Present the contact-message confirmation and the visitor's next actions.
 *
 * @example
 * <SuccessContactMessage />
 */
export function SuccessContactMessage() {
  return (
    <div className="relative min-h-[calc(100vh-5rem)] overflow-hidden bg-gray-50/70 dark:bg-gray-900">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-emerald-400/10 blur-3xl" />
      </div>

      <div className="container relative mx-auto px-4 py-8 md:py-12">
        <Breadcrumb
          items={[
            { text: trans("home.title"), url: URLS.home },
            { text: trans("contactUs"), url: URLS.contactUs },
          ]}
        />

        <main className="flex min-h-[65vh] items-center justify-center py-10">
          <section
            aria-live="polite"
            className="w-full max-w-xl rounded-3xl border border-gray-200/80 bg-white px-6 py-10 text-center shadow-xl shadow-gray-200/60 dark:border-gray-700 dark:bg-gray-800 dark:shadow-black/20 sm:px-10 sm:py-12"
          >
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-emerald-50 ring-8 ring-emerald-50/60 dark:bg-emerald-950/50 dark:ring-emerald-950/20">
              <CheckCircle2
                aria-hidden="true"
                className="h-12 w-12 text-emerald-600 dark:text-emerald-400"
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                {trans("contactSuccess.received")}
              </p>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
                {trans("contactSuccess.title")}
              </h1>
              <p className="mx-auto max-w-md text-base leading-7 text-gray-600 dark:text-gray-300">
                {trans("contactSuccess.description")}
              </p>
            </div>

            <div className="my-8 flex items-center justify-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/70 px-4 py-4 text-sm text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-300">
              <Clock3 aria-hidden="true" className="h-5 w-5 shrink-0" />
              <span>{trans("contactSuccess.replyTiming")}</span>
            </div>

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
              <Button
                variant="outline"
                size="lg"
                className="w-full gap-2 sm:w-auto"
                onClick={() => contactUsAtom.update(false)}
              >
                <MessageSquarePlus aria-hidden="true" className="h-5 w-5" />
                {trans("contactSuccess.sendAnother")}
              </Button>

              <BaseLink
                to={URLS.home}
                className="w-full hover:no-underline sm:w-auto"
              >
                <Button size="lg" className="w-full gap-2 sm:w-auto">
                  <Home aria-hidden="true" className="h-5 w-5" />
                  {trans("contactSuccess.returnHome")}
                </Button>
              </BaseLink>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
